import { logger } from "@/lib/logger";
/**
 * Hook to automatically process offline file queue when online
 * CRASH-SAFE: All navigator/window access wrapped in try-catch
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { offlineFileQueue } from '@/utils/offlineFileQueue';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { safeIsOnline, hasWindow } from '@/lib/browserSafeUtils';

export function useOfflineFileQueueProcessor() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [queueCount, setQueueCount] = useState(0);
  const processingRef = useRef(false);

  const updateQueueCount = useCallback(async () => {
    try {
      const count = await offlineFileQueue.getQueueCount();
      setQueueCount(count);
    } catch (error) {
      logger.warn('[useOfflineFileQueueProcessor] Failed to get queue count:', error);
      setQueueCount(0);
    }
  }, []);

  const processQueue = useCallback(async () => {
    // Use ref to prevent race conditions
    if (processingRef.current || !safeIsOnline()) return;

    try {
      processingRef.current = true;
      setIsProcessing(true);
      
      const items = await offlineFileQueue.getAllItems();
      const pendingItems = items.filter(item => item.status === 'pending');

      if (pendingItems.length === 0) {
        return;
      }

      logger.debug(`Processing ${pendingItems.length} queued file uploads...`);
      let successCount = 0;
      let failCount = 0;

      for (const item of pendingItems) {
        try {
          // Update status to processing
          await offlineFileQueue.updateItem(item.id, { status: 'processing' });

          // Upload to Supabase storage
          const fileName = `${item.patientId}/${item.documentType}/${Date.now()}_${item.fileName}`;
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('medical-documents')
            .upload(fileName, item.file, {
              cacheControl: '3600',
              upsert: false,
            });

          if (uploadError) throw uploadError;

          // Create OCR job record
          const { data: { user } } = await supabase.auth.getUser();
          const { data: ocrJob, error: jobError } = await supabase
            .from('document_ocr_jobs')
            .insert({
              patient_id: item.patientId,
              trial_site_id: item.trialSiteId,
              document_type: item.documentType,
              storage_path: fileName,
              file_name: item.fileName,
              file_size_bytes: item.fileSize,
              mime_type: item.fileType,
              created_by: user?.id,
            })
            .select()
            .single();

          if (jobError) throw jobError;

          // Trigger OCR processing
          const { error: ocrError } = await supabase.functions.invoke(
            'process-document-ocr',
            {
              body: {
                ocr_job_id: ocrJob.id,
                storage_path: fileName,
              },
            }
          );

          if (ocrError) {
            logger.warn('OCR processing failed, but file uploaded:', ocrError);
          }

          // Remove from queue on success
          await offlineFileQueue.removeItem(item.id);
          successCount++;
          logger.debug(`Successfully processed queued file: ${item.fileName}`);
        } catch (error: any) {
          logger.error(`Failed to process queued file: ${item.fileName}`, error);
          failCount++;

          // Update with error
          try {
            await offlineFileQueue.updateItem(item.id, {
              status: 'failed',
              retryCount: item.retryCount + 1,
              errorMessage: error.message,
            });

            // Remove if max retries reached
            if (item.retryCount >= 2) {
              await offlineFileQueue.removeItem(item.id);
              logger.debug(`Max retries reached for ${item.fileName}, removing from queue`);
            }
          } catch (updateError) {
            logger.warn('[useOfflineFileQueueProcessor] Failed to update item status:', updateError);
          }
        }
      }

      // Show summary toast
      if (successCount > 0) {
        toast.success('Offline files synced', {
          description: `${successCount} document(s) uploaded successfully`,
        });
      }
      if (failCount > 0) {
        toast.error('Some uploads failed', {
          description: `${failCount} document(s) could not be uploaded`,
        });
      }
    } catch (error) {
      logger.error('[useOfflineFileQueueProcessor] Error processing queue:', error);
    } finally {
      processingRef.current = false;
      setIsProcessing(false);
      await updateQueueCount();
    }
  }, [updateQueueCount]);

  useEffect(() => {
    // Initial count - wrapped in try-catch
    try {
      updateQueueCount();
    } catch (error) {
      logger.warn('[useOfflineFileQueueProcessor] Failed initial count:', error);
    }

    // Listen for online event
    const handleOnline = () => {
      logger.debug('Connection restored, processing file queue...');
      processQueue();
    };

    // Listen for queue updates
    const handleQueueUpdate = () => {
      updateQueueCount();
    };

    // Safe event listener registration
    try {
      if (hasWindow()) {
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline-file-queue-updated', handleQueueUpdate);
      }
    } catch (error) {
      logger.warn('[useOfflineFileQueueProcessor] Failed to setup listeners:', error);
    }

    // Process on mount if online
    if (safeIsOnline()) {
      processQueue();
    }

    return () => {
      // Safe event listener cleanup
      try {
        if (hasWindow()) {
          window.removeEventListener('online', handleOnline);
          window.removeEventListener('offline-file-queue-updated', handleQueueUpdate);
        }
      } catch (error) {
        logger.warn('[useOfflineFileQueueProcessor] Failed to cleanup listeners:', error);
      }
    };
  }, [updateQueueCount, processQueue]);

  return {
    isProcessing,
    queueCount,
    processQueue,
  };
}
